import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { SelectField } from 'components';
import validate from 'validate.js';
import { Grid, TextField } from '@mui/material';
import { SetFormState } from '../../../actions/IncidentTrackerActions';

const schema = {
	genericType: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	type: {
		presence: { allowEmpty: false, message: 'is required' },
	}
};

const useStyles = makeStyles((theme) => ({
	root: {},
	container: {
		marginTop: theme.spacing(3)
	},
	fields: {
		margin: theme.spacing(-1),
		display: 'flex',
		flexWrap: 'wrap',
		'& > *': {
			flexGrow: 1,
			margin: theme.spacing(1),
		},
	},
	submitButton: {
		marginTop: theme.spacing(2),
		width: '100%',
	},
	divider: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},
	noBorder: {
		border: 'none',
	}
}));

const IncidentConfigurationTypeForm = (props) => {
	const classes = useStyles();
	const {
		onChange,
		disabled,
		genericTypes,
		defaultConfig = undefined,
		...rest
	} = props;
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.incidentTrackerReducer.formState);

	const updateFormState = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _getGenericTypeId = (genericTypeName) => {
		const genericType = genericTypes.find(genType => genType.text === genericTypeName);
		return genericType?.value;
	};

	const [selectedGenericType, setSelectedGenericType] = useState(_getGenericTypeId(defaultConfig?.genericType)); // id of generic type
	const [type, setType] = useState(defaultConfig?.type ?? '');
	const id = defaultConfig?.id;

	const _handleGenericTypeChange = (event) => {
		setSelectedGenericType(event.target.value);
		updateFormState(event);
		onChange({ id, dependencyId: event.target.value, value: type, name: 'type' });
	};

	const _handleTypeChange = (event) => {
		setType(event.target.value);
		updateFormState(event);
		onChange({ id, dependencyId: selectedGenericType, value: event.target.value, name: 'type' });
	};

	useEffect(() => {
		const errors = validate(formState.values, schema);
		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
		});
	}, [formState.values]);

	useEffect(() => {
		setFormState({
			...formState,
			values: {
				genericType: selectedGenericType,
				type
			}
		});
	}, []);

	const hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));

	return (
		<Grid
			className={classes.container}
			container
			md={12}
			xs={12}
			spacing="8"
		>
			<Grid
				item
				md={6}
				xs={6}
				className={classes.fields}
			>
				<SelectField
					id="genericType"
					name="genericType"
					hintText="Generic Type"
					disabled={disabled}
					menuItems={genericTypes || []}
					value={selectedGenericType || ''}
					onChange={_handleGenericTypeChange}
					formControlStyle={{ width: 'auto' }}
					error={hasError('genericType') ? formState.errors.genericType[0] : null}
				/>
			</Grid>
			<Grid item md={6} xs={6}>
				<TextField
					id="type"
					name="type"
					label="Type"
					hintText="Add a value"
					variant="outlined"
					fullWidth
					disabled={!selectedGenericType || disabled}
					value={type || ''}
					onChange={_handleTypeChange}
					error={hasError('type')}
					helperText={
						hasError('type') ? formState.errors.type[0] : null
					}
				/>
			</Grid>
		</Grid>
	);
};

IncidentConfigurationTypeForm.propTypes = {
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	genericTypes: PropTypes.array,
	defaultConfig: PropTypes.object
};

export default IncidentConfigurationTypeForm;
